<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue';
import {
  listIdentities,
  currentIdentity,
  setIdentity,
  type OfflineIdentity,
} from '@/common/auth/offlineAuthProvider';
import { getUrls } from '@/common/composables/url/url';

/**
 * Offline-only control strip, pinned bottom-left.
 *
 * Two jobs: say unmistakably that this is not a real environment, and let you
 * switch which fixture person you are signed in as. Persona is the thing most
 * worth exercising in this app — super_admin, admin, support and a plain key
 * holder each see a different sidebar, different routes and different write
 * controls — and offline is the only place you can flip between them freely.
 *
 * Rendered by App.vue solely when VITE_OFFLINE_MODE is true.
 */

const identities = ref<OfflineIdentity[]>([]);
const active = ref<OfflineIdentity | null>(null);
const open = ref(false);
const llmBackend = ref<string>('checking…');

const ROLE_LABELS: Record<OfflineIdentity['role'], string> = {
  super_admin: 'Super admin',
  admin: 'Admin',
  support: 'Support',
  user: 'User',
};

const grouped = computed(() => {
  const order: OfflineIdentity['role'][] = ['super_admin', 'admin', 'support', 'user'];
  return order
    .map((role) => ({ role, people: identities.value.filter((i) => i.role === role) }))
    .filter((group) => group.people.length > 0);
});

onMounted(async () => {
  identities.value = listIdentities();
  active.value = currentIdentity();

  try {
    const { apiUrlBase } = getUrls();
    const response = await fetch(`${apiUrlBase}/offline/status`);
    const body = await response.json();
    llmBackend.value =
      body.llm?.backend === 'ollama' ? `ollama · ${body.llm.model}` : 'echo (no Ollama)';
  } catch {
    llmBackend.value = 'API unreachable';
  }
});

function choose(identity: OfflineIdentity) {
  if (identity.kerb === active.value?.kerb) {
    open.value = false;
    return;
  }
  setIdentity(identity.kerb);
}
</script>

<template>
  <div class="offline-dock">
    <Transition name="pop">
      <div v-if="open" class="offline-menu">
        <div class="offline-menu-head">Sign in as</div>
        <div v-for="group in grouped" :key="group.role" class="offline-group">
          <div class="offline-group-label">{{ ROLE_LABELS[group.role] }}</div>
          <button
            v-for="identity in group.people"
            :key="identity.kerb"
            type="button"
            class="offline-option"
            :class="{ 'is-active': identity.kerb === active?.kerb }"
            @click="choose(identity)"
          >
            <span class="offline-option-name">{{ identity.displayName }}</span>
            <span class="offline-option-kerb">{{ identity.kerb }}</span>
            <span class="offline-option-meta">{{ identity.affiliation }}</span>
          </button>
        </div>
        <div class="offline-menu-foot">
          LLM: {{ llmBackend }}
          <br />
          Switching reloads the app.
        </div>
      </div>
    </Transition>

    <button type="button" class="offline-badge" @click="open = !open">
      <span class="offline-dot" aria-hidden="true" />
      <span class="offline-label">OFFLINE</span>
      <span v-if="active" class="offline-who">
        {{ active.kerb }} · {{ ROLE_LABELS[active.role] }}
      </span>
      <i class="pi" :class="open ? 'pi-chevron-down' : 'pi-chevron-up'" aria-hidden="true" />
    </button>
  </div>
</template>

<style scoped>
.offline-dock {
  position: fixed;
  left: 16px;
  bottom: 16px;
  z-index: 900;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
}

.offline-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border: 1px solid rgba(163, 31, 52, 0.35);
  border-radius: 999px;
  background: var(--surface);
  color: var(--ink);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  font: inherit;
}

.offline-badge:hover {
  border-color: rgba(163, 31, 52, 0.6);
}

.offline-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 0 3px rgba(163, 31, 52, 0.15);
}

.offline-label {
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--accent);
  font-size: 10.5px;
}

.offline-who {
  color: var(--ink-2, var(--ink-3));
  font-size: 11.5px;
}

.offline-badge .pi {
  font-size: 10px;
  color: var(--ink-2, var(--ink-3));
}

.offline-menu {
  width: 268px;
  max-height: 62vh;
  overflow-y: auto;
  background: var(--surface);
  border: 1px solid var(--border-2);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);
  padding: 8px;
}

.offline-menu-head {
  padding: 6px 8px 8px;
  font-weight: 600;
  color: var(--ink);
}

.offline-group-label {
  padding: 8px 8px 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--ink-3, var(--ink-4));
}

.offline-option {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0 8px;
  width: 100%;
  text-align: left;
  padding: 7px 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  font: inherit;
}

.offline-option:hover {
  background: var(--surface-2);
}

.offline-option.is-active {
  background: rgba(163, 31, 52, 0.08);
}

.offline-option-name {
  font-weight: 500;
  color: var(--ink);
}

.offline-option-kerb {
  color: var(--ink-2, var(--ink-3));
  font-size: 11px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.offline-option-meta {
  grid-column: 1 / -1;
  color: var(--ink-3, var(--ink-4));
  font-size: 10.5px;
}

.offline-menu-foot {
  padding: 10px 8px 4px;
  margin-top: 4px;
  border-top: 1px solid var(--border-2);
  color: var(--ink-3, var(--ink-4));
  font-size: 10.5px;
  line-height: 1.5;
}

.pop-enter-active,
.pop-leave-active {
  transition:
    opacity 0.14s ease,
    transform 0.14s ease;
}

.pop-enter-from,
.pop-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
